import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import { toast } from "sonner";
import { BookOpen, CalendarDays, Download, ArrowRight, LineChart, FolderKanban, FlaskConical } from "lucide-react";
import { CURRENCIES, PLANS, formatPrice } from "@/lib/pricing";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Landing() {
  const [currency, setCurrency] = useState("INR");

  // Surface any auth error written by AuthCallback on failed login.
  useEffect(() => {
    const err = window.sessionStorage.getItem("auth_error");
    if (err) {
      window.sessionStorage.removeItem("auth_error");
      toast.error(err, { duration: 6000 });
    }
  }, []);

  const handleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950 text-zinc-900 dark:text-zinc-50">
      {/* Top nav */}
      <header className="border-b border-zinc-200 dark:border-zinc-800 bg-white/60 dark:bg-zinc-950/60 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5" data-testid="brand-logo">
            <Logo size={32} />
            <span className="font-sans font-bold tracking-tight text-lg">
              Resy<span className="text-blue-600">Note</span>
            </span>
            <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 border border-zinc-200 dark:border-zinc-800 rounded px-1.5 py-0.5 ml-2">
              v0.3
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogin}
              data-testid="nav-signin-btn"
              className="font-body"
            >
              Sign in
            </Button>
            <Button
              size="sm"
              onClick={handleLogin}
              data-testid="nav-get-started-btn"
              className="bg-blue-600 hover:bg-blue-700 text-white font-body"
            >
              Start free trial
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-50 pointer-events-none" aria-hidden />
        <div className="max-w-6xl mx-auto px-6 py-20 md:py-28 grid md:grid-cols-2 gap-12 items-center relative">
          <div className="space-y-7">
            <div className="inline-flex items-center gap-2 border border-zinc-200 dark:border-zinc-800 rounded-full px-3 py-1 text-xs font-mono text-zinc-600 dark:text-zinc-400 bg-white/50 dark:bg-zinc-900/50">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              15-day free trial · no card required
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-sans font-bold tracking-tight leading-[1.05]">
              A research notebook
              <br />
              <span className="text-blue-600">that keeps up</span> with you.
            </h1>
            <p className="text-base md:text-lg font-body text-zinc-600 dark:text-zinc-400 max-w-xl leading-relaxed">
              Plan experiments, track protocols, log observations, and export
              manuscript-ready reports. Designed for PhD scholars and lab groups
              who deserve better than spreadsheets.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button
                size="lg"
                onClick={handleLogin}
                data-testid="hero-cta-primary"
                className="bg-blue-600 hover:bg-blue-700 text-white font-body"
              >
                Sign in with Google
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <a
                href="#features"
                className="inline-flex items-center px-4 py-2 text-sm font-body text-zinc-700 dark:text-zinc-300 hover:text-zinc-900 dark:hover:text-zinc-50 transition-colors"
                data-testid="hero-cta-secondary"
              >
                See features ↓
              </a>
            </div>
            <div className="flex items-center gap-6 pt-2 text-xs font-mono text-zinc-500">
              <span>● PDF / DOCX export</span>
              <span>● Dark mode</span>
              <span>● Cloud sync</span>
            </div>
          </div>

          {/* Hero panel */}
          <div className="relative">
            <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md shadow-sm overflow-hidden">
              <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/80">
                <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
                <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
                <span className="ml-3 text-[11px] font-mono text-zinc-500">resynote.app / experiments</span>
              </div>
              <div className="p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-sans text-sm font-semibold">Thesis – Chapter 4</h3>
                  <span className="text-[10px] font-mono uppercase tracking-widest text-emerald-600 border border-emerald-200 bg-emerald-50 dark:bg-emerald-950/40 dark:border-emerald-800 dark:text-emerald-400 rounded px-1.5 py-0.5">
                    on track
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: "Planned", value: 4, color: "bg-zinc-400" },
                    { label: "In Progress", value: 3, color: "bg-blue-500" },
                    { label: "Completed", value: 11, color: "bg-emerald-500" },
                    { label: "Failed", value: 1, color: "bg-red-500" },
                  ].map((s) => (
                    <div key={s.label} className="border border-zinc-200 dark:border-zinc-800 rounded p-3">
                      <div className="flex items-center gap-2">
                        <span className={`h-2 w-2 rounded-full ${s.color}`} />
                        <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{s.label}</span>
                      </div>
                      <div className="text-2xl font-sans font-bold mt-1">{s.value}</div>
                    </div>
                  ))}
                </div>
                <div className="border-t border-zinc-200 dark:border-zinc-800 pt-3 space-y-2">
                  {[
                    { t: "Optimize PCR annealing temperature", s: "In Progress" },
                    { t: "Western blot – replicate 3", s: "Completed" },
                    { t: "qPCR primer validation", s: "Planned" },
                  ].map((e) => (
                    <div key={e.t} className="flex items-center justify-between text-sm font-body">
                      <span className="truncate">{e.t}</span>
                      <span className="text-[10px] font-mono text-zinc-500">{e.s}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="absolute -bottom-4 -right-4 h-24 w-24 bg-blue-600/20 blur-3xl rounded-full" aria-hidden />
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-6xl mx-auto px-6 py-20 border-t border-zinc-200 dark:border-zinc-800">
        <div className="mb-12">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-blue-600 mb-3">Built for researchers</div>
          <h2 className="text-3xl md:text-4xl font-sans font-bold tracking-tight max-w-2xl">
            Every tool your lab notebook never had.
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-0 border border-zinc-200 dark:border-zinc-800 rounded-md overflow-hidden">
          {[
            { Icon: FlaskConical, title: "Experiment management", body: "Title, objective, hypothesis, materials, step-by-step protocol. Duplicate or template in one click." },
            { Icon: FolderKanban, title: "Projects & folders", body: "Group experiments under thesis, papers or long-running projects. Stay organized across years." },
            { Icon: CalendarDays, title: "Timeline & reminders", body: "Schedule incubations, follow-ups, and deadlines. Never miss a critical time point again." },
            { Icon: BookOpen, title: "Notes & version history", body: "Timestamped lab notes. Every edit tracked. Reproducibility without the overhead." },
            { Icon: LineChart, title: "Search, filter, analytics", body: "Find anything in seconds. Monitor success rate, throughput and progress trends." },
            { Icon: Download, title: "Manuscript-ready export", body: "Export experiments to PDF or DOCX formatted for supervisors, papers, and archives." },
          ].map(({ Icon, title, body }) => (
            <div key={title} className="p-7 border-b md:border-b lg:border-b border-r border-zinc-200 dark:border-zinc-800 last:border-b-0 hover:bg-white dark:hover:bg-zinc-900/50 transition-colors">
              <Icon className="h-5 w-5 text-blue-600 mb-4" />
              <h3 className="font-sans font-semibold text-lg mb-1.5">{title}</h3>
              <p className="text-sm font-body text-zinc-600 dark:text-zinc-400 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section className="max-w-6xl mx-auto px-6 py-20 border-t border-zinc-200 dark:border-zinc-800">
        <div className="mb-10 flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-blue-600 mb-3">Pricing</div>
            <h2 className="text-3xl md:text-4xl font-sans font-bold tracking-tight">
              Simple, honest, academic-friendly.
            </h2>
          </div>
          {/* Currency toggle */}
          <div
            className="inline-flex rounded-md border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-1"
            role="tablist"
            aria-label="Select currency"
            data-testid="currency-toggle"
          >
            {CURRENCIES.map((c) => {
              const active = currency === c.code;
              return (
                <button
                  key={c.code}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setCurrency(c.code)}
                  data-testid={`currency-option-${c.code.toLowerCase()}`}
                  className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest rounded transition-colors ${
                    active
                      ? "bg-zinc-900 dark:bg-zinc-50 text-white dark:text-zinc-950"
                      : "text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-50"
                  }`}
                >
                  {c.symbol} {c.label}
                </button>
              );
            })}
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {PLANS.map((p) => {
            const price = formatPrice(p, currency);
            const otherCurrencies = CURRENCIES.filter((c) => c.code !== currency);
            const isTrial = p.id === "trial";
            return (
              <div
                key={p.id}
                className={`p-7 border rounded-md ${
                  p.featured
                    ? "border-blue-600 bg-blue-50/50 dark:bg-blue-950/20 dark:border-blue-500"
                    : "border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900"
                }`}
                data-testid={`pricing-plan-${p.id}`}
              >
                <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3">{p.tier}</div>
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-sans font-bold" data-testid={`pricing-price-${p.id}`}>{price}</span>
                  {p.suffix && !isTrial && (
                    <span className="text-sm font-mono text-zinc-500">{p.suffix}</span>
                  )}
                </div>
                {!isTrial && (
                  <div className="mt-1 text-xs font-mono text-zinc-500" data-testid={`pricing-alt-${p.id}`}>
                    {otherCurrencies.map((c) => `${c.symbol}${p.prices[c.code]}`).join(" · ")}
                  </div>
                )}
                <div className="text-sm font-body text-zinc-600 dark:text-zinc-400 mt-2 mb-5">{p.note}</div>
                <Button
                  onClick={handleLogin}
                  data-testid={`pricing-cta-${p.id}`}
                  className={p.featured ? "w-full bg-blue-600 hover:bg-blue-700 text-white" : "w-full bg-white dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 text-zinc-900 dark:text-zinc-100 hover:bg-zinc-50 dark:hover:bg-zinc-800"}
                >
                  {p.cta}
                </Button>
              </div>
            );
          })}
        </div>
      </section>

      <footer className="border-t border-zinc-200 dark:border-zinc-800 py-10">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <Logo size={28} />
            <div className="mt-3 text-sm font-body text-zinc-600 dark:text-zinc-400">
              <span className="font-sans font-semibold">ResyNote</span> — a research notebook for the
              modern lab.
            </div>
            <div className="mt-1 text-xs font-mono text-zinc-500">
              Owned & operated by <span className="font-semibold text-zinc-700 dark:text-zinc-300">THREENNOVATORS LLP</span>
            </div>
          </div>
          <div className="text-right space-y-1">
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500">Contact us</div>
            <a
              href="mailto:hello@resynote.com"
              className="font-sans font-medium text-blue-600 hover:underline block"
              data-testid="contact-email-link"
            >
              hello@resynote.com
            </a>
            <div className="flex justify-end gap-3 pt-2 text-xs font-mono text-zinc-500">
              <Link to="/contact" className="hover:text-zinc-900 dark:hover:text-zinc-50" data-testid="footer-link-contact">Contact</Link>
              <span>·</span>
              <Link to="/terms" className="hover:text-zinc-900 dark:hover:text-zinc-50" data-testid="footer-link-terms">Terms</Link>
              <span>·</span>
              <Link to="/refund-policy" className="hover:text-zinc-900 dark:hover:text-zinc-50" data-testid="footer-link-refund">Refund Policy</Link>
            </div>
            <div className="text-xs font-mono text-zinc-500 pt-1">© 2026 THREENNOVATORS LLP · All rights reserved.</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
